import React, { useRef, useState } from 'react';
import { Alert, AlertTitle, Box, Chip, IconButton, List, ListItem, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ACCEPT_ATTRIBUTE, SUBMISSION_FILE_TYPE_LABEL, SUBMISSION_LIMITS, SubmissionFileType, getFileTypeByName } from '../../../../model/ser/ser-enums';
import { formatFileSize } from '../../../../utils/format';
import { FieldError } from '../../../../components/form-page-parts';
import { FileTypeIcon } from '../../components/file-type-icon';
import { RejectedFile, SubmissionDraft } from '../submission-send-rules';
import { StepSection } from './step-section';

const ACCEPTED_GROUPS = [
    { type: SubmissionFileType.Pdf, extensions: 'PDF' },
    { type: SubmissionFileType.Word, extensions: 'DOC, DOCX' },
    { type: SubmissionFileType.Excel, extensions: 'XLS, XLSX' },
    { type: SubmissionFileType.Imagem, extensions: 'JPG, PNG, TIFF' },
];

function Dropzone({ onFiles, disabled }: { onFiles: (files: File[]) => void; disabled?: boolean }) {
    const inputRef = useRef<HTMLInputElement>(null);
    // Contador evita o "pisca" do destaque ao passar sobre elementos filhos
    const dragDepth = useRef(0);
    const [dragging, setDragging] = useState(false);

    function open() {
        if (!disabled) inputRef.current?.click();
    }

    function handleDrop(e: React.DragEvent) {
        e.preventDefault();
        dragDepth.current = 0;
        setDragging(false);
        if (!disabled && e.dataTransfer.files.length) onFiles(Array.from(e.dataTransfer.files));
    }

    return (
        <Box
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-label="Adicionar arquivos"
            aria-disabled={disabled}
            onClick={open}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), open())}
            onDragEnter={(e) => { e.preventDefault(); dragDepth.current += 1; setDragging(true); }}
            onDragLeave={() => { dragDepth.current -= 1; if (dragDepth.current <= 0) setDragging(false); }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            sx={(theme) => ({
                py: { xs: 4, md: 5 }, px: 2, textAlign: 'center', borderRadius: 3,
                cursor: disabled ? 'not-allowed' : 'pointer',
                border: '2px dashed', borderColor: dragging ? 'primary.main' : 'divider',
                bgcolor: dragging ? alpha(theme.palette.primary.main, 0.08) : alpha(theme.palette.primary.main, 0.02),
                opacity: disabled ? 0.6 : 1,
                transition: 'border-color 150ms ease-out, background-color 150ms ease-out',
                '&:hover': disabled ? undefined : { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.05) },
                '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
            })}
        >
            <Box
                sx={{
                    mx: 'auto', mb: 1.5, width: 64, height: 64, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: 'primary.main', color: 'primary.contrastText',
                    transform: dragging ? 'scale(1.08)' : 'scale(1)', transition: 'transform 150ms ease-out',
                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                }}
            >
                <CloudUploadIcon fontSize="large" />
            </Box>
            <Typography variant="subtitle1" fontWeight={700}>
                {dragging ? 'Solte os arquivos aqui' : 'Arraste os arquivos ou clique para selecionar'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Até {SUBMISSION_LIMITS.maxFilesPerSubmission} arquivos por remessa, com até {SUBMISSION_LIMITS.maxFileSizeMb} MB cada
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                {ACCEPTED_GROUPS.map((group) => (
                    <Chip key={group.type} size="small" variant="outlined" icon={<Box sx={{ display: 'flex', ml: 0.5 }}><FileTypeIcon type={group.type} size={18} /></Box>} label={group.extensions} />
                ))}
            </Box>
            <input
                ref={inputRef}
                type="file"
                multiple
                hidden
                accept={ACCEPT_ATTRIBUTE}
                onChange={(e) => {
                    if (e.target.files?.length) onFiles(Array.from(e.target.files));
                    e.target.value = '';
                }}
            />
        </Box>
    );
}

interface FilesStepProps {
    draft: SubmissionDraft;
    rejected: RejectedFile[];
    error?: string;
    onAdd: (files: File[]) => void;
    onRemove: (index: number) => void;
    onDismissRejected: () => void;
}

export function FilesStep({ draft, rejected, error, onAdd, onRemove, onDismissRejected }: FilesStepProps) {
    const totalSize = draft.files.reduce((sum, file) => sum + file.size, 0);

    return (
        <StepSection title="Arquivos" description="Envie os documentos que devem ser publicados. O nome original de cada arquivo é mantido.">
            <Dropzone onFiles={onAdd} />
            <FieldError message={error} />

            {rejected.length > 0 && (
                <Alert severity="warning" onClose={onDismissRejected} sx={{ mt: 2 }}>
                    <AlertTitle>{rejected.length === 1 ? '1 arquivo não foi adicionado' : `${rejected.length} arquivos não foram adicionados`}</AlertTitle>
                    {rejected.map((item, index) => (
                        <Typography key={`${item.name}-${index}`} variant="body2"><strong>{item.name}</strong>: {item.reason}</Typography>
                    ))}
                </Alert>
            )}

            {draft.files.length > 0 && (
                <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight={700}>
                            {draft.files.length === 1 ? '1 arquivo' : `${draft.files.length} arquivos`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {formatFileSize(totalSize)} · {draft.files.length}/{SUBMISSION_LIMITS.maxFilesPerSubmission}
                        </Typography>
                    </Box>
                    <List disablePadding sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                        {draft.files.map((file, index) => {
                            const type = getFileTypeByName(file.name);
                            return (
                                <ListItem
                                    key={`${file.name}-${file.size}-${index}`}
                                    divider={index < draft.files.length - 1}
                                    sx={{ gap: 1.5, py: 1.25 }}
                                    secondaryAction={
                                        <Tooltip title="Remover arquivo">
                                            <IconButton edge="end" aria-label={`Remover ${file.name}`} onClick={() => onRemove(index)}>
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        </Tooltip>
                                    }
                                >
                                    <FileTypeIcon type={type} />
                                    <Box sx={{ minWidth: 0, pr: 4 }}>
                                        <Typography variant="body2" fontWeight={600} noWrap title={file.name}>{file.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {type ? SUBMISSION_FILE_TYPE_LABEL[type] : 'Arquivo'} · {formatFileSize(file.size)}
                                        </Typography>
                                    </Box>
                                </ListItem>
                            );
                        })}
                    </List>
                </Box>
            )}
        </StepSection>
    );
}
